import { useEffect, useMemo } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  ExistingClientPanel,
  NewClientPanel,
} from './ShareCarePlanPickerPanels';
import { ShareCarePlanSuccess } from './ShareCarePlanSuccess';
import {
  MODE_LABEL,
  type DeliveryMode,
  type PickerTab,
  type SharePickerClient,
} from './share-care-plan-modal.types';

type ShareCarePlanPickerStepProps = {
  mode: DeliveryMode;
  pickerTab: PickerTab;
  onTabChange: (tab: PickerTab) => void;
  search: string;
  onSearchChange: (next: string) => void;
  isLoadingClients: boolean;
  clients: SharePickerClient[];
  selectedClientId: string | null;
  onSelectClient: (id: string | null) => void;
  newClientName: string;
  onNewClientNameChange: (next: string) => void;
  newClientEmail: string;
  onNewClientEmailChange: (next: string) => void;
  /** True when in-app delivery is picked on the new-client tab (not allowed). */
  inAppNewBlocked: boolean;
  /**
   * When set, in-app sharing is locked to this attributed client (one care
   * seeker per plan). Replaces the recipient picker with a read-only notice.
   */
  lockedClient?: SharePickerClient | null;
  onSend: () => void;
  isSending: boolean;
  isSuccess: boolean;
  errorMessage: string | null;
  onBack: () => void;
  onClose: () => void;
};

/**
 * Step 2 of the Share Care Plan modal: pick the recipient (existing
 * client search OR new-client invite form), then Send. Send triggers the
 * real share request; this step renders the in-flight (Sending…), error,
 * and success states, and auto-closes shortly after a successful share.
 */
export const ShareCarePlanPickerStep = ({
  mode,
  pickerTab,
  onTabChange,
  search,
  onSearchChange,
  isLoadingClients,
  clients,
  selectedClientId,
  onSelectClient,
  newClientName,
  onNewClientNameChange,
  newClientEmail,
  onNewClientEmailChange,
  inAppNewBlocked,
  lockedClient,
  onSend,
  isSending,
  isSuccess,
  errorMessage,
  onBack,
  onClose,
}: ShareCarePlanPickerStepProps) => {
  const isLocked = Boolean(lockedClient);
  const newClientReady =
    newClientEmail.trim().length > 3 && newClientEmail.includes('@');
  const canSend =
    !isSending &&
    // Locked share still needs a resolved client id; the enabled state must
    // match what handleSend/buildPayload can actually send (else click no-ops).
    (isLocked
      ? Boolean(selectedClientId)
      : !inAppNewBlocked &&
        (pickerTab === 'existing' ? Boolean(selectedClientId) : newClientReady));

  const recipientLabel = useMemo(() => {
    if (lockedClient) return lockedClient.name || lockedClient.email || 'your client';
    if (pickerTab === 'existing') {
      const match = clients.find((c) => c.id === selectedClientId);
      return match?.name || match?.email || 'your client';
    }
    return newClientName.trim() || newClientEmail.trim() || 'your client';
  }, [lockedClient, pickerTab, clients, selectedClientId, newClientName, newClientEmail]);

  useEffect(() => {
    if (!isSuccess) return;
    const t = window.setTimeout(() => onClose(), 1800);
    return () => window.clearTimeout(t);
  }, [isSuccess, onClose]);

  if (isSuccess) {
    return <ShareCarePlanSuccess recipientLabel={recipientLabel} mode={mode} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm text-sage hover:text-[hsl(169_95%_18%)]"
          aria-label="Back to delivery method"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          {MODE_LABEL[mode]}
        </button>
      </div>

      {isLocked ? (
        <div
          className="rounded-md border border-sage/30 bg-sage-bg px-3 py-3 text-sm"
          role="status"
        >
          <p className="font-medium text-text-primary">
            This care guide is assigned to {recipientLabel}.
          </p>
          <p className="mt-1 text-text-secondary">
            In-app sharing goes to them. To send it to someone else, go back and choose Email.
          </p>
        </div>
      ) : (
        <>
          <PickerTabBar pickerTab={pickerTab} onTabChange={onTabChange} />

          <div
            key={pickerTab}
            className={`motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-150 motion-safe:fill-mode-both ${
              pickerTab === 'existing'
                ? 'motion-safe:slide-in-from-left-1'
                : 'motion-safe:slide-in-from-right-1'
            }`}
          >
            {pickerTab === 'existing' ? (
              <ExistingClientPanel
                search={search}
                onSearchChange={onSearchChange}
                isLoadingClients={isLoadingClients}
                clients={clients}
                selectedClientId={selectedClientId}
                onSelectClient={onSelectClient}
              />
            ) : (
              <NewClientPanel
                newClientName={newClientName}
                onNewClientNameChange={onNewClientNameChange}
                newClientEmail={newClientEmail}
                onNewClientEmailChange={onNewClientEmailChange}
              />
            )}
          </div>

          {inAppNewBlocked && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800" role="status">
              In-app delivery needs an existing bQuest account. Switch to email to invite a new client.
            </p>
          )}
        </>
      )}

      {errorMessage && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
          {errorMessage}
        </p>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={isSending}>
          Cancel
        </Button>
        <Button
          onClick={onSend}
          disabled={!canSend}
          className="bg-[#026857] text-white hover:bg-[#025247] disabled:opacity-50"
        >
          {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
          {isSending
            ? 'Sending…'
            : mode === 'email'
              ? 'Send email'
              : 'Send in-app'}
        </Button>
      </div>
    </div>
  );
};

type PickerTabBarProps = {
  pickerTab: PickerTab;
  onTabChange: (tab: PickerTab) => void;
};

const PickerTabBar = ({ pickerTab, onTabChange }: PickerTabBarProps) => (
  <div className="flex items-center gap-2 border-b border-border-subtle">
    {(['existing', 'new'] as const).map((tab) => {
      const label = tab === 'existing' ? 'Existing clients' : 'Invite a new client';
      const isActive = pickerTab === tab;
      return (
        <button
          key={tab}
          type="button"
          onClick={() => onTabChange(tab)}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
            isActive
              ? 'border-sage text-sage'
              : 'border-transparent text-text-secondary hover:text-text-primary'
          }`}
        >
          {label}
        </button>
      );
    })}
  </div>
);
